<template>
  <div
    class="app-calendar-field"
    :class="{ 'app-calendar-field--readonly': isReadonly }"
    v-click-outside="closeDropdown"
    ref="root"
  >
    <vc-date-picker
      v-model="innerValue"
      @input="isDropdown ? setDropdownDate($event) : ''"
      class="app-date-picker"
      :model-config="modelConfig"
      :min-date="minDate"
      :max-date="maxDate"
      :popover="{ visibility: isDropdown ? 'hidden' : 'click' }"
      :mode="mode"
      :update-on-input="false"
      :is-range="isRange"
      is24hr
    >
      <template
        v-slot="{ inputValue, inputEvents, updateValue, togglePopover }"
      >
        <label
          class="app-calendar-field__main"
          :class="{
            'app-calendar-field__main--focused': isFocused,
          }"
          @click="isDropdown ? openDropdown() : togglePopover()"
        >
          <app-title-box
            v-if="title"
            class="app-calendar-field__title"
            size="small"
            :title="title"
            :isTooltip="!!tooltip"
            :tooltip="tooltip"
            :isRequired="isRequired"
          ></app-title-box>
          <div v-if="isRange" class="app-calendar-field__range-wrapper">
            <input
              :value="inputValue.start"
              v-on="inputEvents.start"
              ref="inputRangeStart"
              placeholder="01.01.2021"
              class="app-calendar-field__range-input"
              v-autowidth="{ minWidth: '60px', comfortZone: 0 }"
            />
            <span class="app-calendar-field__range-delimiter"> - </span>
            <input
              :value="inputValue.end"
              v-on="inputEvents.end"
              ref="inputRangeEnd"
              placeholder="01.02.2021"
              class="app-calendar-field__range-input"
              v-autowidth="{ minWidth: '60px', comfortZone: 0 }"
            />
          </div>
          <input
            v-else
            class="app-calendar-field__input"
            type="text"
            :placeholder="placeholder"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="
              updateValue($event.target.value, {
                hidePopover: false,
                formatInput: true,
              })
            "
            v-on="inputEvents"
            :value="inputValue"
            :disabled="isDisabled"
            :readonly="isReadonly"
            ref="input"
          />
          <app-svg-icon
            v-if="isIcon"
            isCurrentColor
            class="app-calendar-field__icon"
            name="calendar"
          ></app-svg-icon>
        </label>
      </template>
    </vc-date-picker>

    <template v-if="isDropdown">
      <slide-y-up-transition>
        <div
          v-show="isOpen"
          class="app-calendar-field__dropdown"
          ref="dropdownBody"
        >
          <div class="app-calendar-field__dropdown-header">
            <h3 class="app-calendar-field__dropdown-header-title">
              Выберите период
            </h3>
            <button
              @click="closeDropdown"
              class="app-calendar-field__dropdown-header-close"
            ></button>
          </div>
          <vc-date-picker
            v-model="innerValue"
            @input="isOpen = false"
            class="app-date-picker app-calendar-field__dropdown-datepicker"
            :model-config="modelConfig"
            :min-date="minDate"
            :max-date="maxDate"
            :popover="{ visibility: 'click' }"
            :mode="mode"
            :update-on-input="false"
            :is-range="isRange"
            is24hr
            ref="dropdownDatePicker"
            :columns="responsive.isMobile ? 1 : 2"
          >
          </vc-date-picker>
          <div class="app-calendar-field__dropdown-footer">
            <app-button-control
              @click="setPeriod(periodForMonth())"
              class="app-calendar-field__dropdown-footer-btn"
              :isDisabled="isCurrentMonth"
              :class="{
                'app-calendar-field__dropdown-footer-btn--active': isCurrentMonth,
              }"
            >
              Текущий месяц
            </app-button-control>
            <app-button-control
              @click="setPeriod(periodForMonth(new Date().setDate(0)))"
              class="app-calendar-field__dropdown-footer-btn"
              :isDisabled="isLastMonth"
              :class="{
                'app-calendar-field__dropdown-footer-btn--active': isLastMonth,
              }"
            >
              Прошлый месяц
            </app-button-control>
            <app-button-control
              @click="setPeriod(periodForYear())"
              class="app-calendar-field__dropdown-footer-btn"
              :isDisabled="isCurrentYear"
              :class="{
                'app-calendar-field__dropdown-footer-btn--active': isCurrentYear,
              }"
            >
              Текущий год
            </app-button-control>
            <app-button-control
              @click="setPeriod(periodForYear(1))"
              class="app-calendar-field__dropdown-footer-btn"
              :isDisabled="isLastYear"
              :class="{
                'app-calendar-field__dropdown-footer-btn--active': isLastYear,
              }"
            >
              Прошлый год
            </app-button-control>
            <app-button-control
              @click="clearDate"
              class="app-calendar-field__dropdown-footer-btn"
            >
              Сбросить
            </app-button-control>
          </div>
        </div>
      </slide-y-up-transition>
    </template>
  </div>
</template>

<script>
import Cleave from 'cleave.js';
import { SlideYUpTransition } from 'vue2-transitions';
import { periodForYear, periodForMonth } from '@/helpers/getPeriod';
import { setPositionLeftDropdown } from '@/helpers';

export default {
  name: 'AppCalendarField',
  inject: ['responsive'],
  components: {
    AppSvgIcon: () => import('@/components/common/AppSvgIcon'),
    AppTitleBox: () => import('@/components/common/AppTitleBox'),
    AppButtonControl: () => import('@/components/ui/AppButtonControl'),
    SlideYUpTransition,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    isRange: {
      type: Boolean,
    },
    isReadonly: {
      type: Boolean,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    isIcon: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'date',
    },
    tooltip: {
      type: String,
      default: '',
    },
    maxDate: {
      default: undefined,
    },
    minDate: {
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Date, Object],
      default: '',
    },
    modelConfig: {
      type: Object,
      default() {
        if (this.isRange) {
          return {
            start: {
              type: 'string',
              mask: 'YYYY-MM-DD',
            },
            end: {
              type: 'string',
              mask: 'YYYY-MM-DD',
            },
          };
        }
        return {
          type: 'string',
          mask: 'YYYY-MM-DD',
        };
      },
    },
    isDropdown: {
      type: Boolean,
    },
  },
  data: () => ({
    isFocused: false,
    isOpen: false,
    innerValue: '',
    date: {
      date: true,
      datePattern: ['d', 'm', 'Y'],
      delimiter: '.',
    },
  }),
  computed: {
    isCurrentYear() {
      if (!this.isDropdown) return false;
      return (
        JSON.stringify(this.innerValue) === JSON.stringify(this.periodForYear())
      );
    },
    isLastYear() {
      if (!this.isDropdown) return false;
      return (
        JSON.stringify(this.innerValue) ===
        JSON.stringify(this.periodForYear(1))
      );
    },
    isCurrentMonth() {
      if (!this.isDropdown) return false;
      return (
        JSON.stringify(this.innerValue) ===
        JSON.stringify(this.periodForMonth())
      );
    },
    isLastMonth() {
      if (!this.isDropdown) return false;
      return (
        JSON.stringify(this.innerValue) ===
        JSON.stringify(this.periodForMonth(new Date().setDate(0)))
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.innerValue = value;
      },
    },
    innerValue: {
      immediate: true,
      deep: true,
      handler(value) {
        if (this.isRange) {
          this.$emit('input', value);
        } else {
          let date = '';
          if (value && this.modelConfig.mask === 'YYYY-MM-DD') {
            date = this.$options.filters.dateFormat(value, 'yyyy-MM-dd');
          } else {
            date = value;
          }
          this.$emit('input', date);
        }
      },
    },
  },
  // TODO: сделать автоматическое позиционирование дропдауна
  mounted() {
    if (this.isDropdown) {
      window.addEventListener('resize', this.changePositionDropdown);
    }
    if (this.isRange) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const inputRangeStart = new Cleave(this.$refs.inputRangeStart, {
        delimiters: ['.', '.', ' '],
        blocks: [2, 2, 4],
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const inputRangeEnd = new Cleave(this.$refs.inputRangeEnd, {
        delimiters: ['.', '.', ' '],
        blocks: [2, 2, 4],
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const c = new Cleave(this.$refs.input, {
        delimiters: ['.', '.', ' '],
        blocks: [2, 2, 4],
      });
    }
  },
  beforeDestroy() {
    if (this.isDropdown) {
      window.removeEventListener('resize', this.changePositionDropdown);
    }
  },
  methods: {
    periodForYear,
    periodForMonth,
    setPositionLeftDropdown,
    /**
     * Измениение позиции дропдауна
     */
    changePositionDropdown() {
      if (this.isOpen) {
        this.$refs.dropdownBody.style.left = this.setPositionLeftDropdown(
          this.$refs.root,
          this.$refs.dropdownBody
        );
      }
    },
    /**
     * Установка периода через кнопки
     * @param period
     */
    setPeriod(period) {
      this.setDropdownDate(period);
      this.innerValue = { ...period };
      this.closeDropdown();
    },
    /**
     * Очистка датапикера
     */
    clearDate() {
      this.innerValue = null;
      const { dropdownDatePicker } = this.$refs;
      dropdownDatePicker.move(new Date());
      this.closeDropdown();
    },
    /**
     * Установка значения датапикера в дропдауне
     * @param value
     */
    setDropdownDate(value) {
      if (value?.end) {
        const { dropdownDatePicker } = this.$refs;
        dropdownDatePicker.move(value.end);
      }
    },
    /**
     * Открытие дропдауна
     */
    openDropdown() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.changePositionDropdown();
      });
      this.$emit('open');
    },
    /**
     * Закрытие дропдауна
     */
    closeDropdown() {
      this.isOpen = false;
      this.$emit('close');
    },
    /**
     * Действие на событие change
     * @param event
     */
    handleChange(event) {
      this.$emit('change', event);
    },
    /**
     * Действие на событие blur
     * @param event
     */
    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },
    /**
     * Действие на событие focus
     * @param event
     */
    handleFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
    },
  },
};
</script>

<style lang="scss" scoped>
.app-calendar-field {
  $root: &;

  position: relative;
  width: 100%;

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 7px 12px;
    cursor: pointer;
    background: $white;
    border: 1px solid $gray-light;
    border-radius: 2px;
    transition: border $A;

    &:hover {
      border-color: $color-brand-green;
    }

    &--focused {
      border-color: $color-brand-green;
    }
  }

  &__title {
    margin-bottom: 8px;
  }

  &__range-wrapper {
    display: flex;
  }

  &__range-delimiter {
    padding: 0 5px;
    font-size: rem(14);
    font-weight: 300;
    line-height: 18px;
  }

  &__range-input {
    padding: 0;
    font-size: rem(14);
    font-weight: 300;
    line-height: 18px;
    color: $black;
    letter-spacing: 0.02em;
    cursor: pointer;
    background-color: $color-transparent;
    border: 0;

    &::placeholder {
      color: $gray-dark;
    }
  }

  &__input {
    padding: 0;
    font-size: rem(14);
    font-weight: 250;
    line-height: 22px;
    color: $black;
    letter-spacing: 0.02em;
    cursor: pointer;
    background-color: $color-transparent;
    border: 0;

    &::placeholder {
      color: $gray-dark;
    }

    &:disabled {
      background: $white;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px $color-white inset !important;
    }
  }

  &-icon {
    position: absolute;
    top: 50%;
    right: 20px;
    width: 12px;
    height: 13px;
    stroke-width: 0;
    transform: translateY(-50%);
  }

  &__dropdown {
    @include main-container-box;

    position: absolute;
    top: 60px;
    z-index: 5;
    width: 615px;
    padding: 0 20px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 2px;
    box-shadow: 0 20px 20px rgba(0, 0, 0, 0.25);

    @include media-xs() {
      width: 311px;
    }

    @include media-xxs() {
      right: 0;
      left: auto;
    }
  }

  &__dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 69px;
    border-bottom: 1px solid #eee;
  }

  &__dropdown-header-title {
    margin: 0;
    font-size: rem(13);
    font-weight: 300;
    line-height: 16px;
    color: $black;
  }

  &__dropdown-header-close {
    position: relative;
    right: -8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    color: $color-white;
    cursor: pointer;
    background-color: $color-transparent;
    border: 0;
    transition: color $A;

    &::before,
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 13px;
      height: 1px;
      content: '';
      background: rgba($color: $black, $alpha: 0.3);
      transition: background-color $A;
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    &:hover {
      &::before,
      &::after {
        background: rgba($color: $black, $alpha: 1);
      }
    }
  }

  &__dropdown-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 1px solid #eee;
  }

  &__dropdown-footer-btn {
    width: 133px;
    padding: 0;
    // margin-right: 10px;
    margin-bottom: 8px;

    &:last-of-type {
      margin-right: 0;
    }

    &--active {
      cursor: default;
      background: $color-tag-filter-bg !important;
      border-color: transparent;

      &:hover {
        background: $color-tag-filter-bg !important;
      }
    }
  }
}
</style>
